import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

// Postal → 2-digit state FIPS.
const STATE_FIPS: Record<string, string> = {
  AL: "01", AK: "02", AZ: "04", AR: "05", CA: "06", CO: "08", CT: "09",
  DE: "10", DC: "11", FL: "12", GA: "13", HI: "15", ID: "16", IL: "17",
  IN: "18", IA: "19", KS: "20", KY: "21", LA: "22", ME: "23", MD: "24",
  MA: "25", MI: "26", MN: "27", MS: "28", MO: "29", MT: "30", NE: "31",
  NV: "32", NH: "33", NJ: "34", NM: "35", NY: "36", NC: "37", ND: "38",
  OH: "39", OK: "40", OR: "41", PA: "42", RI: "44", SC: "45", SD: "46",
  TN: "47", TX: "48", UT: "49", VT: "50", VA: "51", WA: "53", WV: "54",
  WI: "55", WY: "56", PR: "72",
};

export type QctCountyRow = {
  countyFips: string; // 5-digit county FIPS
  count: number;
  sampleTracts: string[]; // first few tract IDs (11-digit GEOID)
};

export type StateQctResult = {
  state: string;
  year: number;
  total: number;
  byCounty: QctCountyRow[];
};

const QCT_URL =
  "https://services.arcgis.com/VTyQ9soqVukalItT/arcgis/rest/services/QUALIFIED_CENSUS_TRACTS_2026/FeatureServer/0/query";

/**
 * Qualified Census Tracts for a state — LIHTC / DDA-adjacent SEO data.
 * Returns per-county QCT counts + a sample tract list.
 */
export const getStateQct = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ state: z.string().length(2) }).parse(input))
  .handler(async ({ data }): Promise<StateQctResult> => {
    const postal = data.state.toUpperCase();
    const sfips = STATE_FIPS[postal];
    if (!sfips) return { state: postal, year: 2026, total: 0, byCounty: [] };

    const features: Array<{ attributes: Record<string, unknown> }> = [];
    let offset = 0;
    const pageSize = 1000;
    for (let page = 0; page < 10; page++) {
      const url = `${QCT_URL}?f=json&where=STATE%3D%27${sfips}%27&outFields=GEOID,STATE,COUNTY,NAME&returnGeometry=false&resultOffset=${offset}&resultRecordCount=${pageSize}`;
      const res = await fetch(url);
      if (!res.ok) break;
      const json = (await res.json()) as { features?: typeof features; exceededTransferLimit?: boolean };
      const chunk = json.features ?? [];
      features.push(...chunk);
      if (!json.exceededTransferLimit || chunk.length < pageSize) break;
      offset += chunk.length;
    }

    const map = new Map<string, QctCountyRow>();
    for (const f of features) {
      const a = f.attributes;
      const state = String(a.STATE ?? "");
      const county = String(a.COUNTY ?? "");
      const geoid = String(a.GEOID ?? "");
      if (!state || !county) continue;
      const countyFips = `${state}${county}`;
      let row = map.get(countyFips);
      if (!row) {
        row = { countyFips, count: 0, sampleTracts: [] };
        map.set(countyFips, row);
      }
      row.count++;
      if (row.sampleTracts.length < 5 && geoid) row.sampleTracts.push(geoid);
    }

    const byCounty = [...map.values()].sort((a, b) => b.count - a.count);
    return { state: postal, year: 2026, total: features.length, byCounty };
  });

// ---------------- Difficult Development Areas (DDA) ----------------

const DDA_URL =
  "https://services.arcgis.com/VTyQ9soqVukalItT/arcgis/rest/services/Difficult_Development_Areas_2026/FeatureServer/0/query";

const FIPS_TO_POSTAL: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_FIPS).map(([k, v]) => [v, k]),
);

type DdaRaw = { ZCTA5: string | null; DDA_CODE: string | null; DDA_TYPE: string | null; DDA_NAME: string | null };

export type DdaEntry = { type: "Metro" | "Non-Metro"; name: string; zcta?: string; code: string };
export type StateDdaResult = { state: string; year: number; total: number; metro: DdaEntry[]; nonMetro: DdaEntry[] };

let ddaCache: { at: number; rows: DdaRaw[] } | null = null;

async function fetchAllDda(): Promise<DdaRaw[]> {
  if (ddaCache && Date.now() - ddaCache.at < 6 * 60 * 60 * 1000) return ddaCache.rows;
  const all: DdaRaw[] = [];
  let offset = 0;
  const pageSize = 2000;
  for (let page = 0; page < 20; page++) {
    const url = `${DDA_URL}?f=json&where=1%3D1&outFields=ZCTA5,DDA_CODE,DDA_TYPE,DDA_NAME&returnGeometry=false&resultOffset=${offset}&resultRecordCount=${pageSize}`;
    const res = await fetch(url);
    if (!res.ok) break;
    const json = (await res.json()) as { features?: Array<{ attributes: DdaRaw }>; exceededTransferLimit?: boolean };
    const chunk = json.features ?? [];
    all.push(...chunk.map((f) => f.attributes));
    if (!json.exceededTransferLimit || chunk.length < pageSize) break;
    offset += chunk.length;
  }
  ddaCache = { at: Date.now(), rows: all };
  return all;
}

function ddaStatePostal(r: DdaRaw): string | null {
  const name = r.DDA_NAME ?? "";
  const m = name.match(/,\s*([A-Z]{2})(?:\s|$|,)/);
  if (m) return m[1];
  const code = r.DDA_CODE ?? "";
  if (code.startsWith("NCNTY") && code.length >= 7) {
    const fips2 = code.slice(5, 7);
    return FIPS_TO_POSTAL[fips2] ?? null;
  }
  const nMatch = code.match(/N(\d{5})$/);
  if (nMatch) return FIPS_TO_POSTAL[nMatch[1].slice(0, 2)] ?? null;
  return null;
}

export const getStateDda = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ state: z.string().length(2) }).parse(input))
  .handler(async ({ data }): Promise<StateDdaResult> => {
    const postal = data.state.toUpperCase();
    const rows = await fetchAllDda();
    const metro: DdaEntry[] = [];
    const nonMetro: DdaEntry[] = [];
    for (const r of rows) {
      if (ddaStatePostal(r) !== postal) continue;
      const entry: DdaEntry = {
        type: r.DDA_CODE?.startsWith("NCNTY") ? "Non-Metro" : "Metro",
        name: r.DDA_NAME ?? "Unknown",
        zcta: r.ZCTA5 ?? undefined,
        code: r.DDA_CODE ?? "",
      };
      if (entry.type === "Metro") metro.push(entry);
      else nonMetro.push(entry);
    }
    metro.sort((a, b) => (a.zcta ?? "").localeCompare(b.zcta ?? ""));
    // Dedupe non-metro county entries (one row per polygon)
    const seen = new Set<string>();
    const nmDedup = nonMetro.filter((e) => (seen.has(e.code) ? false : (seen.add(e.code), true)));
    return { state: postal, year: 2026, total: metro.length + nmDedup.length, metro, nonMetro: nmDedup };
  });

// Note: HUD's official IL/FMR API at huduser.gov is behind Akamai and blocks
// datacenter IP ranges (returns nginx 403 regardless of Bearer token). We use
// the public ArcGIS layers above instead (services.arcgis.com), which are
// unauthenticated and unblocked.

